/**
 * Universal Upload — cross-device parity landing.
 *
 * Founder direction (2026-07-26 · verbatim):
 *   "The Universal Upload system should behave consistently on iPhone,
 *    Android, Windows, Mac, iPad, external USB drives, SD cards, cloud
 *    storage providers. Users should be able to import multiple files
 *    naturally wherever the operating system allows it. The objective
 *    is one upload experience across every supported device."
 *   "Option 2 — ONE central /upload page with the dropzone plus the
 *    13 purpose tiles inline."
 *
 * Flow (identical on every OS):
 *   1  Big dropzone at the top. User can EITHER drag files from
 *      Finder/Explorer/USB/SD card OR tap "Choose files" for the
 *      native multi-picker (iOS-safe shape: `multiple`, no `accept`
 *      on iOS Safari, no `display:none`, no `capture`).
 *   2  When files are staged, the purpose tiles below become the
 *      "categorise and import" step. Tap a tile → files stashed via
 *      lib/pendingFiles, navigate to /import-todays-recordings with
 *      the purpose in the URL. The import page picks them up on mount
 *      — no re-picking, no re-dropping.
 *   3  If the user prefers the classic flow, they can tap a tile
 *      before staging anything and the import page opens empty as
 *      before.
 *
 * All 13 tiles remain unchanged from the prior version.
 */
import React, { useCallback, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Video, Camera, Mic, Receipt, Car, ClipboardCheck,
  Award, Briefcase, BookOpen, Sparkles, Package, Layers,
  UploadCloud,
} from "lucide-react";
import { isIOSSafari } from "../lib/iosDetect";
import { stashPendingFiles } from "../lib/pendingFiles";
import "./UniversalUpload.css";

const IOS_SAFE = typeof window !== "undefined" && isIOSSafari();
const DESKTOP_ACCEPT = "video/*,audio/*,image/*,.mp4,.mov,.m4v,.avi,.mkv,.webm,.ts,.mts,.m2ts,.mxf,.insv,.mp3,.m4a,.wav,.aac,.flac,.ogg,.jpg,.jpeg,.png,.heic,.heif,.webp,.gif,.bmp,.tif,.tiff,.dng,.pdf,.csv,.txt";

const PURPOSE_TILES = [
  { id: "todays-multi-camera", label: "Today's multi-camera recordings", sub: "Everything from the SD card — I'll match the cameras up.", icon: Layers,        purposeCode: "sd_card_teaching_day_intake" },
  { id: "single-camera",       label: "Single-camera recording",         sub: "One camera, one clip.",                                icon: Video,         purposeCode: "single_camera_recording"  },
  { id: "video",               label: "Video",                           sub: "Any video — I'll work out where it belongs.",           icon: Video,         purposeCode: "general_video"            },
  { id: "photograph",          label: "Photograph",                      sub: "Photos from any camera or phone.",                      icon: Camera,        purposeCode: "photograph"               },
  { id: "voice-note",          label: "Voice note",                      sub: "A spoken thought or reflection.",                       icon: Mic,           purposeCode: "voice_note"               },
  { id: "receipt",             label: "Petrol / business receipt",       sub: "I'll capture the amount, date and category.",           icon: Receipt,       purposeCode: "business_receipt"         },
  { id: "vehicle-doc",         label: "Vehicle document",                sub: "MOT, insurance, tax, service records.",                 icon: Car,           purposeCode: "vehicle_document"         },
  { id: "lesson-evidence",     label: "Lesson evidence",                 sub: "Anything from a driving lesson.",                       icon: ClipboardCheck, purposeCode: "lesson_evidence"         },
  { id: "test-evidence",       label: "Driving test evidence",           sub: "Photos or clips from a test day.",                      icon: Award,         purposeCode: "driving_test_evidence"    },
  { id: "business-doc",        label: "Business document",               sub: "Contracts, invoices, statements.",                      icon: Briefcase,     purposeCode: "business_document"        },
  { id: "resource",            label: "Educational resource",            sub: "PDFs, articles or reference material.",                 icon: BookOpen,      purposeCode: "educational_resource"     },
  { id: "intelligence",        label: "Intelligence contribution",       sub: "Something the whole community should learn from.",      icon: Sparkles,      purposeCode: "intelligence_contribution" },
  { id: "other",               label: "Something else",                  sub: "Not sure? I'll still take it and work it out.",         icon: Package,       purposeCode: "other"                    },
];

export default function UniversalUpload() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [staged, setStaged] = useState([]);   // Array<File>
  const [dragActive, setDragActive] = useState(false);

  const stageFiles = useCallback((fileList) => {
    if (!fileList || fileList.length === 0) return;
    setStaged((prev) => [...prev, ...Array.from(fileList)]);
  }, []);

  const clearStaged = useCallback(() => setStaged([]), []);

  const openBatch = useCallback((tile) => {
    if (staged.length > 0) stashPendingFiles(staged);
    const q = new URLSearchParams({ purpose: tile.purposeCode, label: tile.label }).toString();
    navigate(`/import-todays-recordings?${q}`);
  }, [staged, navigate]);

  const onDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) stageFiles(e.dataTransfer.files);
  }, [stageFiles]);

  const onDragOver = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(true);
  }, []);
  const onDragLeave = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
  }, []);

  return (
    <main data-testid="universal-upload-page" className="universal-upload-page">
      <header className="universal-upload-header">
        <Link to="/" data-testid="universal-upload-back-link" className="universal-upload-back">← Home</Link>
        <h1 data-testid="universal-upload-title">Universal Upload</h1>
        <p className="universal-upload-hint">
          Drop files first, or pick a purpose to open the picker directly.
        </p>
      </header>

      {/* Central dropzone — laptop parity with phone flow --------- */}
      <section
        className={`uu-dropzone ${dragActive ? "is-drag" : ""} ${staged.length > 0 ? "is-staged" : ""}`}
        data-testid="universal-upload-dropzone"
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
      >
        <UploadCloud className="uu-dropzone__icon" aria-hidden="true" />
        {staged.length === 0 ? (
          <>
            <strong>Drop files here from Finder, Explorer, USB, SD card or cloud storage.</strong>
            <p>Multiple files welcome — I&apos;ll match them up.</p>
            <button
              type="button"
              className="uu-dropzone__pick"
              onClick={() => fileInputRef.current?.click()}
              data-testid="universal-upload-choose-files"
            >Choose files</button>
          </>
        ) : (
          <>
            <strong data-testid="universal-upload-staged-count">
              {staged.length} file{staged.length === 1 ? "" : "s"} ready.
            </strong>
            <p>Pick a tile below to categorise and import them.</p>
            <div className="uu-dropzone__actions">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="universal-upload-add-more"
              >Add more</button>
              <button
                type="button"
                className="uu-dropzone__clear"
                onClick={clearStaged}
                data-testid="universal-upload-start-over"
              >Start over</button>
            </div>
          </>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          {...(IOS_SAFE ? {} : { accept: DESKTOP_ACCEPT })}
          /* Offscreen 1px opacity:0 shape — iOS Safari refuses to
           * activate display:none inputs. Never `capture`. */
          style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none", overflow: "hidden" }}
          aria-hidden="true"
          tabIndex={-1}
          onChange={(e) => { stageFiles(e.target.files); try { e.target.value = ""; } catch { /* noop */ } }}
          onInput={(e) => { if (e.target.files?.length) stageFiles(e.target.files); }}
          data-testid="universal-upload-input"
          data-multiple="true"
          data-ios-safe={IOS_SAFE ? "true" : "false"}
        />
      </section>

      {/* Purpose tiles ------------------------------------------- */}
      <ul className="universal-upload-grid" data-testid="universal-upload-grid" role="list">
        {PURPOSE_TILES.map((tile) => {
          const Icon = tile.icon;
          return (
            <li key={tile.id}>
              <button
                type="button"
                onClick={() => openBatch(tile)}
                data-testid={`universal-upload-tile-${tile.id}`}
                className="universal-upload-tile"
                aria-label={tile.label}
              >
                <span className="universal-upload-tile__iconwrap" aria-hidden="true">
                  <Icon className="universal-upload-tile__icon" />
                </span>
                <span className="universal-upload-tile__body">
                  <span className="universal-upload-tile__label">{tile.label}</span>
                  <span className="universal-upload-tile__sub">{tile.sub}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </main>
  );
}
